import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DoCheck,
  effect,
  forwardRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { HxBaseControl } from '../../../base/hx-base-control';
import { createValidationState, ValidationState } from '../../../base/validation-state';
import { HxCheckbox } from '../../checkbox/checkbox';
import { DateAdapter } from '../../date-picker/adapters/abstract-date-adapter';
import { HxDatePicker } from '../../date-picker/date-picker';
import { provideNativeDateAdapter } from '../../date-picker/date-picker-providers';
import { HX_DATE_ADAPTER } from '../../date-picker/date-picker-tokens';
import { HxFormGroup } from '../../form-group/form-group';
import { HxError, HxHint } from '../../form-group/index';
import { HxInput } from '../../input';
import { HxLabel } from '../../label/label';
import { CheckboxOption } from './checkbox-option';

let nextUniqueId = 0;

/** Selected value object containing both value and label */
export interface SelectedCheckboxValue {
  value: string;
  label: string;
  reason?: string;
  date?: Date | null;
}

/**
 * Harmony Checkbox With Label Component
 *
 * A wrapper component that composes HxFormGroup + HxCheckbox to create multi-checkbox form fields with labels, hints,
 * and validation.
 *
 * Features:
 *
 * - Multiple checkbox options with question label
 * - Horizontal and vertical layouts
 * - Angular Forms integration (ControlValueAccessor)
 * - Min/max selection validation
 * - Pre-selected options support
 * - WCAG 2.1 AA accessibility
 *
 * @example
 *   ```html
 *   <!-- Basic usage -->
 *   <hx-checkbox-with-label
 *     question="Select your interests"
 *     [options]="interests">
 *   </hx-checkbox-with-label>
 *
 *   <!-- With validation -->
 *   <hx-checkbox-with-label
 *     question="Select 2-4 skills"
 *     [options]="skills"
 *     [min]="2"
 *     [max]="4"
 *     [required]="true">
 *   </hx-checkbox-with-label>
 *
 *   <!-- Reactive forms -->
 *   <hx-checkbox-with-label
 *     formControlName="preferences"
 *     question="Your preferences"
 *     [options]="options">
 *   </hx-checkbox-with-label>
 *   ```;
 */
@Component({
  selector: 'hx-checkbox-with-label',
  templateUrl: './checkbox-with-label.html',
  styleUrls: ['./checkbox-with-label.css'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgTemplateOutlet,
    ReactiveFormsModule,
    HxCheckbox,
    HxDatePicker,
    HxFormGroup,
    HxLabel,
    HxHint,
    HxError,
    HxInput,
  ],
  host: {
    '[class.hx-checkbox-with-label]': 'true',
    '[class.hx-checkbox-with-label--grid]': '_getValidColumns() !== null',
    '[class.hx-checkbox-with-label--grid-horizontal]': '_getValidColumns() !== null && orientation() === "horizontal"',
    '[class.hx-checkbox-with-label--grid-vertical]': '_getValidColumns() !== null && orientation() === "vertical"',
    '[class.hx-checkbox-with-label--justify]':
      'justify() && orientation() === "horizontal" && _getValidColumns() === null',
    '[class.hx-checkbox-with-label--label-horizontal]': 'labelHorizontally() && orientation() === "horizontal"',
    '[style.--checkbox-with-label-columns]': '_getValidColumns()',
    '[class.hx-checkbox-with-label--readonly]': 'readonly()',
  },
  providers: [
    provideNativeDateAdapter(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxCheckboxWithLabel),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => HxCheckboxWithLabel),
      multi: true,
    },
  ],
})
export class HxCheckboxWithLabel extends HxBaseControl implements Validator, DoCheck {
  /** Unique ID for this component instance */
  protected readonly _uniqueId = `hx-checkbox-with-label-${nextUniqueId++}`;

  // ============================================================================
  // INPUTS
  // ============================================================================

  /** Question/label text for the checkbox group */
  question = input('');

  /** Sub-label text displayed below the question label */
  readonly subLabel = input<string>('');

  /** Array of checkbox options to display */
  options = input.required<CheckboxOption[]>();

  /**
   * Layout orientation for the checkbox group.
   *
   * - 'vertical': Stacks checkboxes in a column (default)
   * - 'horizontal': Arranges checkboxes in a row
   *
   * @default 'vertical'
   */
  readonly orientation = input<'horizontal' | 'vertical'>('vertical');

  /**
   * Number of columns for grid layout (1, 2, 3, or 4). When set, checkboxes render in a CSS Grid with the specified
   * column count. When undefined, uses flexbox layout based on orientation.
   *
   * @example
   *   ```html
   *   <!-- 3-column grid layout -->
   *   <hx-checkbox-with-label
   *     question="Select options"
   *     [options]="options"
   *     [columns]="3">
   *   </hx-checkbox-with-label>
   *   ```;
   *
   * @default undefined (uses flexbox)
   */
  readonly columns = input<1 | 2 | 3 | 4 | undefined>(undefined);

  /**
   * Evenly distributes checkboxes horizontally across the row using space-evenly. Only applies to horizontal
   * orientation when columns is not set.
   *
   * @default false
   */
  readonly justify = input<boolean>(false);

  /**
   * Places the question label horizontally to the left of the checkbox group. Creates a two-column layout: Question
   * Label (1/3 width) and Checkboxes (2/3 width). Only applies to horizontal orientation.
   *
   * @default false
   */
  readonly labelHorizontally = input<boolean>(false);

  /** Whether to display the question horizontally (inline with options) or vertically (above options) */
  readonly isQuestionHorizontal = input<boolean>(false);

  /** Whether to show a datepicker next to each checkbox option */
  showDatepicker = input<boolean>(false);

  /** Date picker mode: single date or date range (for datepicker when enabled) */
  datePickerMode = input<'single' | 'range'>('single');

  /** Date display format (for datepicker when enabled) */
  displayFormat = input<string>('MM/DD/YYYY');

  /** Restricts date selection to today or dates in the past (for datepicker when enabled) */
  restrictToPastOrToday = input<boolean>(false);

  /** Restricts date selection to today or dates in the future (for datepicker when enabled) */
  restrictToTodayOrFuture = input<boolean>(false);

  /** Restricts date selection to current year only (for datepicker when enabled) */
  restrictToCurrentYear = input<boolean>(false);

  /** Number of years to look back from today for date selection (for datepicker when enabled) */
  lookBackYears = input<number | null>(null);

  /** Minimum selectable date (for datepicker when enabled) */
  minDate = input<string | null>(null);

  /** Maximum selectable date (for datepicker when enabled) */
  maxDate = input<string | null>(null);

  /** Restricts date selection to specific years only (for datepicker when enabled) */
  allowedYears = input<number[] | null>(null);

  /** Whether to show the question label */
  showQuestion = input<boolean>(true);

  /** Whether all checkboxes are disabled */
  disabled = input<boolean>(false);

  /** Whether at least one checkbox must be selected */
  required = input<boolean>(false);

  /** Minimum number of selections required */
  min = input<number | undefined>(undefined);

  /** Maximum number of selections allowed */
  max = input<number | undefined>(undefined);

  /** Hint text displayed below checkbox group */
  hint = input<string>('');

  /** Custom error message for required validation */
  requiredErrorMessage = input<string>('');

  /** Custom error message for min selection validation */
  minErrorMessage = input<string>('');

  /** Custom error message for max selection validation */
  maxErrorMessage = input<string>('');

  /** Custom error message for required reason field */
  reasonRequiredErrorMessage = input<string>('');

  /** Custom error message for required date picker validation when checkbox is selected */
  dateRequiredErrorMessage = input<string>('Please select a date');

  /** Label for "Other" option that triggers reason field */
  otherLabel = input<string>('Other');

  /** Display label for the synthetic "None" option */
  noneLabel = input<string>('None');

  /** Display label for the synthetic "All" option */
  allLabel = input<string>('All');

  /** Label for reason text field */
  reasonLabel = input<string>('Please specify');

  /** Placeholder for reason text field */
  reasonPlaceholder = input<string>('Enter your reason...');

  /**
   * HxInput settings (type, hint, subtitle, rows, minlength, pattern, ...) for the "Other" input; override the defaults
   * above.
   */
  otherInputProps = input<Record<string, any> | null>(null);

  /** Maximum character length for reason text field */
  reasonMaxLength = input<number>(30);

  /** Whether to show input field when "Other" option is selected */
  showInputOnOther = input<boolean>(true);

  /**
   * When true, a synthetic "All" option is prepended to the options list. Selecting it checks all standard options;
   * deselecting it unchecks them all. The state auto-updates when standard options are toggled.
   */
  showAllOption = input<boolean>(false);

  /**
   * When true, a synthetic "None" option is appended to the options list. Selecting it clears all other selections.
   * Selecting any standard option automatically deselects "None".
   */
  showNoneOption = input<boolean>(false);

  /**
   * FormControl for the reason field when "Other" option is selected. Parent must create and manage this control for
   * validation.
   *
   * @default undefined (no external control)
   */
  reasonControl = input<FormControl<string> | null>(null);

  /**
   * Map of FormControls for datepicker fields, keyed by option value. Parent must create and manage these controls for
   * date validation.
   *
   * @default undefined (no external controls)
   */
  dateControls = input<Map<string, FormControl<Date | null>> | null>(null);

  /**
   * Optional template rendered after each checkbox option. Context provides the option value as `$implicit`. Use this
   * to render sub-controls beneath individual checkbox options.
   */
  readonly optionTemplate = input<TemplateRef<{ $implicit: string }> | null>(null);

  /**
   * Position of sub-controls under horizontal layout. 'start' = Rendered below all options (order 2, full width)
   * 'beneath' = Rendered directly beneath the parent option (inline)
   */
  readonly subControlPosition = input<'start' | 'beneath'>('start');

  // ============================================================================
  // OUTPUTS
  // ============================================================================

  /** Emitted when checkbox selection changes */
  valueChange = output<SelectedCheckboxValue[]>();

  // ============================================================================
  // INTERNAL STATE
  // ============================================================================

  /** Internal FormControl for managing selection state */
  protected _internalControl = new FormControl<SelectedCheckboxValue[]>([], { nonNullable: true });

  /** Reactive signal for selected checkbox values */
  protected _selectedValues = signal<Set<string>>(new Set());

  /**
   * Unified validation state - SINGLE SOURCE OF TRUTH Replaces the previous scattered signals (_invalid, _touched,
   * etc.)
   */
  protected _validationState!: ValidationState;

  /** Local signal that mirrors showErrors() for reactive host class binding. */
  protected readonly _showErrors = signal<boolean>(false);

  /** Tracks the previous touched state of external control. Used in DoCheck to detect programmatic touch changes. */
  private _previousExternalTouched = false;

  private _getOtherOption(): CheckboxOption | undefined {
    const otherLabelStr = (this.otherLabel() || 'Other').toLowerCase();
    return this.options().find(
      (opt) =>
        opt.value === 'other' ||
        String(opt.value).toLowerCase() === 'other' ||
        opt.label === this.otherLabel() ||
        String(opt.label).toLowerCase() === otherLabelStr ||
        String(opt.label).toLowerCase() === 'other'
    );
  }

  /** Computed signal for reason field error state */
  protected _reasonFieldHasError = computed(() => {
    // Only show error if showInputOnOther is enabled
    if (!this.showInputOnOther()) {
      return false;
    }
    const otherOption = this._getOtherOption();
    const otherSelected = otherOption && this._selectedValues().has(otherOption.value);
    const reasonControl = this.reasonControl();
    const reasonEmpty = !reasonControl?.value || reasonControl?.value.trim() === '';
    return !!(otherSelected && reasonEmpty && this._validationState.touched());
  });

  /** Computed signal for reason field error message */
  protected _reasonErrorMessage = computed(() => {
    if (this._reasonFieldHasError()) {
      return this.reasonRequiredErrorMessage() || 'Please specify a reason for selecting "Other"';
    }
    return '';
  });

  /** Computed signal for whether "Other" option is selected */
  protected _isOtherSelected = computed(() => {
    const otherOption = this._getOtherOption();
    return !!(otherOption && this._selectedValues().has(otherOption.value));
  });

  /** Computed signal for error state */
  protected _hasError = computed(() => this._showErrors());

  /**
   * Effective options list: includes synthetic "None" (prepended first) and/or "All" (prepended second) options when
   * the corresponding inputs are enabled, alongside the regular options from the `options` input.
   */
  protected _effectiveOptions = computed((): CheckboxOption[] => {
    const otherLabel = this.otherLabel();
    // Separate the "Other" option from standard options so it can be placed last
    const standardOpts = this.options().filter((opt) => opt.label !== otherLabel);
    const otherOpt = this.options().find((opt) => opt.label === otherLabel);

    const result: CheckboxOption[] = [];

    if (this.showNoneOption()) {
      result.push({ value: 'none', label: this.noneLabel() || 'None' });
    }

    if (this.showAllOption()) {
      result.push({ value: 'all', label: this.allLabel() || 'All' });
    }

    result.push(...standardOpts);

    // "Other" always comes last
    if (otherOpt) {
      result.push(otherOpt);
    }

    return result;
  });

  /** Memoized map of options for O(1) lookup instead of repeated find() calls */
  protected _optionsMap = computed(() => {
    const map = new Map(this._effectiveOptions().map((opt) => [opt.value, opt]));
    return map;
  });

  /** Memoized set of valid option values for efficient validation */
  protected _validOptionValues = computed(() => {
    return new Set(this._effectiveOptions().map((opt) => opt.value));
  });

  /**
   * Return column count for CSS variable binding. Returns null when columns is undefined or invalid. Valid values are
   * 1, 2, 3, or 4.
   *
   * @internal
   */
  _getValidColumns(): string | null {
    const cols = this.columns();
    if (cols === undefined || cols < 1 || cols > 4) return null;
    return cols.toString();
  }

  /** Injected DateAdapter for parsing string dates to Date objects */
  private readonly _dateAdapter = inject<DateAdapter<Date>>(HX_DATE_ADAPTER);

  /** Computed signal for effective min date. Handles restrictToCurrentYear, lookBackYears, and explicit min date. */
  protected readonly _effectiveMinDate = computed(() => {
    const today = new Date();

    // If restrictToCurrentYear is enabled, set min to start of current year
    if (this.restrictToCurrentYear()) {
      return new Date(today.getFullYear(), 0, 1); // January 1st of current year
    }

    // If lookBackYears is set, calculate min date based on years back
    const lookBack = this.lookBackYears();
    if (lookBack !== null && lookBack > 0) {
      const minDate = new Date(today);
      minDate.setFullYear(today.getFullYear() - lookBack);
      return minDate;
    }

    // Otherwise use explicit min date if provided
    const value = this.minDate();
    if (!value) {
      return null;
    }

    if (typeof value === 'string' && value.trim()) {
      // Try fromIso8601 first (strict YYYY-MM-DD), then use common formats
      const isoResult = this._dateAdapter.fromIso8601(value);
      if (isoResult) {
        return isoResult;
      }
      // Parse using common date formats
      return this._dateAdapter.parse(value, ['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'M/D/YYYY']);
    }

    return null;
  });

  /** Computed signal for effective max date. Handles restrictToCurrentYear, lookBackYears, and explicit max date. */
  protected readonly _effectiveMaxDate = computed(() => {
    const today = new Date();

    // If restrictToCurrentYear is enabled, set max to end of current year
    if (this.restrictToCurrentYear()) {
      return new Date(today.getFullYear(), 11, 31); // December 31st of current year
    }

    // If lookBackYears is set, max should be today (looking back from today)
    const lookBack = this.lookBackYears();
    if (lookBack !== null && lookBack > 0) {
      return today;
    }

    // Otherwise use explicit max date if provided
    const value = this.maxDate();
    if (!value) {
      return null;
    }

    if (typeof value === 'string' && value.trim()) {
      // Try fromIso8601 first (strict YYYY-MM-DD), then use common formats
      const isoResult = this._dateAdapter.fromIso8601(value);
      if (isoResult) {
        return isoResult;
      }
      // Parse using common date formats
      return this._dateAdapter.parse(value, ['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'M/D/YYYY']);
    }

    return null;
  });

  // ============================================================================
  // CONSTRUCTOR & LIFECYCLE
  // ============================================================================

  constructor() {
    super();

    // Initialize unified validation state - SINGLE SOURCE OF TRUTH
    this._validationState = createValidationState({
      internalControl: this._internalControl,
      getNgControl: () => this.ngControl,
    });

    if (this.ngControl) {
      // Manually register this instance as the value accessor and validator
      // This breaks the DI cycle caused by the NG_VALUE_ACCESSOR token lookup.
      this.ngControl.valueAccessor = this;
    }

    // Sync FormControl validators when inputs change
    effect(() => {
      const control = this._internalControl;
      const validators = this._buildValidators();
      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });

      // Update validation state after validator changes
      this._validationState.updateAfterValueChange();

      this._validatorChangeFn();
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Sync initial state from external control if available
    this._validationState.syncFromExternalControl();
    this._syncShowErrors();

    // Subscribe to external control changes to sync _showErrors signal
    // This handles programmatic changes like form.markAllAsTouched()
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      externalControl.statusChanges?.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        this._syncShowErrors();
      });
    }

    // Re-run this control's validators whenever a dateControl value changes.
    // This ensures the parent form reflects date-picker errors (e.g. subsection status).
    // Also re-emit valueChange so the date value is included in the FHIR QuestionnaireResponse.
    const dateControls = this.dateControls();
    if (dateControls && this.showDatepicker()) {
      for (const [, ctrl] of dateControls) {
        ctrl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
          this._internalControl.updateValueAndValidity({ emitEvent: false });
          this.notifyValidatorChange();
          // Re-emit valueChange with the updated date so parent components receive the new value
          this._updateMainControl();
        });
      }
    }

    // Re-run this control's validators whenever reasonControl value/touch state changes.
    // This ensures the parent form reflects reason field errors (e.g. subsection status).
    // Critical for subsection completion when "Other" reason is selected.
    const reasonControl = this.reasonControl();
    if (reasonControl && this.showInputOnOther()) {
      reasonControl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        this._internalControl.updateValueAndValidity({ emitEvent: false });
        this.notifyValidatorChange();
        this._updateMainControl();
      });

      reasonControl.statusChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        this._internalControl.updateValueAndValidity({ emitEvent: false });
        this.notifyValidatorChange();
      });
    }
  }

  /**
   * DoCheck lifecycle hook to detect programmatic touch state changes. This is critical for handling
   * form.markAllAsTouched() which doesn't trigger statusChanges.
   */
  ngDoCheck(): void {
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      const currentTouched = externalControl.touched;
      // If touched state changed externally (e.g., form.markAllAsTouched())
      if (currentTouched !== this._previousExternalTouched) {
        this._previousExternalTouched = currentTouched;
        this._syncShowErrors();

        // When external control is marked touched, also mark reasonControl as touched
        // This ensures reason field validation errors are properly propagated to parent form
        if (currentTouched) {
          const reasonControl = this.reasonControl();
          if (reasonControl && !reasonControl.touched) {
            reasonControl.markAsTouched();
            reasonControl.updateValueAndValidity();
          }
        }
      }
    }
  }

  /** Syncs the _showErrors signal with validation state */
  private _syncShowErrors(): void {
    this._showErrors.set(this._validationState.showErrors());
  }

  // ============================================================================
  // PUBLIC METHODS
  // ============================================================================

  /** Handles reason field blur event - emits value with updated reason */
  protected _handleReasonBlur(): void {
    const rc = this.reasonControl();
    if (rc && !rc.touched) {
      rc.markAsTouched();
      rc.updateValueAndValidity();
    }
    this._updateMainControl({ markTouched: true });
  }

  protected _handleReasonChange(eventOrValue: any): void {
    const rc = this.reasonControl();
    if (!rc) return;

    // Extract value from event (handles both { value: string } and plain string)
    const newValue = typeof eventOrValue === 'string' ? eventOrValue : (eventOrValue?.value ?? '');

    // Update the reasonControl with the new value
    rc.setValue(newValue, { emitEvent: false });
    this._updateMainControl();
  }

  private _updateMainControl(options?: { markTouched?: boolean }): void {
    const reasonControl = this.reasonControl();
    const reasonValue = reasonControl?.value || '';

    const selectedObjects = Array.from(this._selectedValues()).map((value) => {
      const option = this._optionsMap().get(value);
      const result: SelectedCheckboxValue = {
        value,
        label: option?.label || value,
      };

      // If this is the "Other" option, attach the current reason value
      const otherOpt = this._getOtherOption();
      if (
        value === 'other' ||
        option?.value === 'other' ||
        (otherOpt && value === otherOpt.value) ||
        option?.label === this.otherLabel()
      ) {
        result.reason = reasonValue;
      }

      // If showDatepicker is enabled, include the date value from FormControl
      if (this.showDatepicker()) {
        const dateControls = this.dateControls();
        const dateControl = dateControls?.get(value);
        result.date = dateControl?.value || null;
      }

      return result;
    });

    // Update the primary control that the parent is bound to
    this._internalControl.setValue(selectedObjects, { emitEvent: false });

    if (options?.markTouched) {
      this._internalControl.markAsTouched();
      this._validationState.markTouched();
      this._syncShowErrors();
      this._onTouched();
    }

    this._internalControl.updateValueAndValidity({ emitEvent: false });
    this._validationState.updateAfterValueChange();
    this.notifyValidatorChange();

    // Propagate to Angular Forms and parent Output
    this._onChange(selectedObjects);
    this.valueChange.emit(selectedObjects);
  }
  /** Handles individual checkbox change events Updates selection state and syncs to FormControl */
  protected _handleCheckboxChange(optionValue: string, checked: boolean): void {
    const current = new Set(this._selectedValues());

    if (checked) {
      if (this.showNoneOption() && optionValue === 'none') {
        // Selecting "None" → clear all other selections, keep only 'none'
        current.clear();
        current.add('none');
      } else if (this.showAllOption() && optionValue === 'all') {
        // Selecting "All" → select all enabled standard options + 'all', clear 'none', preserve 'other'
        const otherOption = this.options().find((opt) => opt.label === this.otherLabel());
        const otherWasSelected = !!(otherOption && current.has(otherOption.value));
        current.clear();
        this.options()
          .filter((opt) => !opt.disabled && opt.label !== this.otherLabel())
          .forEach((opt) => current.add(opt.value));
        current.add('all');
        // Restore 'other' if it was already selected
        if (otherWasSelected && otherOption) {
          current.add(otherOption.value);
        }
      } else {
        // Standard option selected → add it and remove 'none' if present
        current.add(optionValue);
        if (this.showNoneOption()) {
          current.delete('none');
        }
        // Auto-update 'all' state based on whether all enabled standard options are selected
        if (this.showAllOption()) {
          const standardOptions = this.options().filter((opt) => !opt.disabled && opt.label !== this.otherLabel());
          const allStandardsSelected =
            standardOptions.length > 0 && standardOptions.every((opt) => current.has(opt.value));
          if (allStandardsSelected) {
            current.add('all');
          } else {
            current.delete('all');
          }
        }
      }
    } else {
      current.delete(optionValue);
      if (this.showAllOption() && optionValue === 'all') {
        // Unchecking "All" → deselect all standard options but preserve 'other'
        this.options()
          .filter((opt) => opt.label !== this.otherLabel())
          .forEach((opt) => current.delete(opt.value));
      } else if (this.showAllOption() && optionValue !== 'other') {
        // Any standard option unchecked → also uncheck 'all'
        current.delete('all');
      }
    }

    // Clear reason text if Other was deselected
    if (!checked) {
      const otherOption = this.options().find((opt) => opt.label === this.otherLabel());
      if (otherOption && optionValue === otherOption.value) {
        const rc = this.reasonControl();
        if (rc) {
          rc.reset('', { emitEvent: false });
        }
      }
    }

    this._selectedValues.set(current);

    // When a checkbox is checked and showDatepicker is enabled, mark the associated
    // dateControl as touched so the datepicker shows validation errors immediately.
    // updateValueAndValidity() is required because markAsTouched() alone does NOT
    // emit statusChanges, which is what the datepicker listens to for touch sync.
    if (checked && this.showDatepicker()) {
      const dateControls = this.dateControls();
      const ctrl = dateControls?.get(optionValue);
      if (ctrl) {
        ctrl.markAsTouched();
        ctrl.updateValueAndValidity();
      }
    }

    this._updateMainControl({ markTouched: true });
  }

  /** Handles checkbox change events from the new checkbox API (selectedValuesChange) */
  protected _handleCheckboxChangeNew(optionValue: string, selectedValues: (string | number)[]): void {
    const isChecked = selectedValues.length > 0;
    this._handleCheckboxChange(optionValue, isChecked);
  }

  /** Gets the FormControl for a specific option's datepicker from the parent-provided map */
  protected _getDateControl(optionValue: string): FormControl<Date | null> | null {
    const dateControls = this.dateControls();
    return dateControls?.get(optionValue) || null;
  }

  // ============================================================================
  // CONTROL VALUE ACCESSOR IMPLEMENTATION
  // ============================================================================

  writeValue(value: SelectedCheckboxValue[] | null): void {
    const validatedValue = this._validateInitialValue(value);
    this._internalControl.setValue(validatedValue, { emitEvent: false });
    const valueSet = new Set(validatedValue.map((v) => v.value));
    this._selectedValues.set(valueSet);

    // Initialize date values in FormControls from incoming data
    if (this.showDatepicker()) {
      const dateControls = this.dateControls();
      validatedValue.forEach((v) => {
        if (v.date && dateControls) {
          const control = dateControls.get(v.value);
          if (control) {
            control.setValue(v.date, { emitEvent: false });
          }
        }
      });
    }

    // Initialize reason value from incoming data
    const reasonControl = this.reasonControl();
    const otherOpt = this._getOtherOption();
    const otherOptionInVal = validatedValue.find(
      (v) => v.value === 'other' || (otherOpt && v.value === otherOpt.value)
    );
    if (otherOptionInVal?.reason !== undefined && reasonControl) {
      reasonControl.setValue(otherOptionInVal.reason, { emitEvent: false });
    }

    // Set initial checked state only on uninitialized null/undefined value (never when value is an explicit empty array [])
    const checkedOptions = this.options().filter((opt) => opt.checked);
    if (checkedOptions.length > 0 && value === null && validatedValue.length === 0) {
      const initialValues = checkedOptions.map((opt) => ({ value: opt.value, label: opt.label }));
      this.writeValue(initialValues);
    }
  }

  registerOnChange(fn: (value: SelectedCheckboxValue[]) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this._internalControl.disable() : this._internalControl.enable();
  }

  // ============================================================================
  // VALIDATOR IMPLEMENTATION
  // ============================================================================

  validate(control: AbstractControl): ValidationErrors | null {
    // Only validate after control has been touched
    if (!control.touched && !this._internalControl.touched) {
      return null;
    }

    let aggregatedErrors = { ...(this._internalControl.errors ?? {}) };

    // Validate reason field if "Other" option is selected
    if (this.showInputOnOther()) {
      const reasonControl = this.reasonControl();
      const otherOption = this._getOtherOption();
      const otherSelected = otherOption && this._selectedValues().has(otherOption.value);

      if (otherSelected && reasonControl && (!reasonControl.value || !reasonControl.value.trim())) {
        aggregatedErrors['reasonRequired'] = true;
      }
    }

    return Object.keys(aggregatedErrors).length > 0 ? aggregatedErrors : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this._validatorChangeFn = fn;
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /** Builds validator array based on component inputs */
  private _buildValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (this.required()) {
      validators.push(Validators.required);
    }

    validators.push(this._minMaxValidator.bind(this));

    return validators;
  }

  /** Custom validator for min/max selection constraints and reason requirement */
  private _minMaxValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.touched) {
      return null;
    }

    const value = control.value as SelectedCheckboxValue[] | null;
    const length = value?.length ?? 0;

    if (this.required() && length === 0) {
      return null;
    }

    if (this.min() && length < this.min()!) {
      return { minSelection: { required: this.min(), actual: length } };
    }

    if (this.max() && length > this.max()!) {
      return { maxSelection: { required: this.max(), actual: length } };
    }

    return null;
  }

  /** Validates initial value contains only valid option values Filters out any values that don't match options array */
  private _validateInitialValue(value: SelectedCheckboxValue[] | null): SelectedCheckboxValue[] {
    if (!value || !Array.isArray(value)) return [];

    return value.filter((v) => this._validOptionValues().has(v.value));
  }

  /** Gets error message for display */
  protected _getErrorMessage(errorType: string): string {
    const errors = this._internalControl.errors;
    if (!errors) return '';

    switch (errorType) {
      case 'required':
        return this.requiredErrorMessage() || 'Please select at least one option';

      case 'minSelection':
        if (this.minErrorMessage()) return this.minErrorMessage();
        return `Please select at least ${errors['minSelection'].required} option(s)`;

      case 'maxSelection':
        if (this.maxErrorMessage()) return this.maxErrorMessage();
        return `Please select no more than ${errors['maxSelection'].required} option(s)`;

      case 'reasonRequired':
        return this.reasonRequiredErrorMessage() || 'Please specify a reason for selecting "Other"';

      case 'reasonFieldHidden':
        return 'Unable to enter reason for "Other" option. Please enable the reason field.';

      default:
        return '';
    }
  }
}
